import { useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { StatCard } from '@/components/dashboard/StatCard';
import { CandidateCard } from '@/components/candidates/CandidateCard';
import { CandidateProfile } from '@/components/candidates/CandidateProfile';
import { ScheduleInterviewModal } from '@/components/modals/ScheduleInterviewModal';
import { AssignTaskModal } from '@/components/modals/AssignTaskModal';
import { candidates, dashboardStats, interviews } from '@/data/mockData';
import type { Candidate } from '@/data/mockData';
import {
  Users,
  UserCheck,
  Calendar,
  Briefcase,
  TrendingUp,
  Clock,
  Video,
  MapPin,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const Index = () => {
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);

  const handleViewProfile = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setProfileOpen(true);
  };

  const handleShortlist = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    // In a real app, this would update the database
  };

  const handleScheduleInterview = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setProfileOpen(false);
    setInterviewOpen(true);
  };

  const handleAssignTask = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setProfileOpen(false);
    setTaskOpen(true);
  };

  const recentCandidates = candidates.slice(0, 3);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back! Here's your hiring overview.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Candidates"
            value={dashboardStats.totalCandidates}
            icon={<Users className="h-6 w-6" />}
            trend={{ value: 12, isPositive: true }}
            variant="primary"
          />
          <StatCard
            title="Shortlisted"
            value={dashboardStats.shortlisted}
            icon={<UserCheck className="h-6 w-6" />}
            variant="accent"
          />
          <StatCard
            title="Interviews Scheduled"
            value={dashboardStats.interviewsScheduled}
            icon={<Calendar className="h-6 w-6" />}
            variant="default"
          />
          <StatCard
            title="Hired This Month"
            value={dashboardStats.hired}
            icon={<Briefcase className="h-6 w-6" />}
            trend={{ value: 25, isPositive: true }}
            variant="success"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Recent Candidates */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Recent Candidates</h2>
              <Button variant="link" className="text-primary">
                View All →
              </Button>
            </div>
            <div className="space-y-4">
              {recentCandidates.map((candidate) => (
                <CandidateCard
                  key={candidate.id}
                  candidate={candidate}
                  onViewProfile={handleViewProfile}
                  onShortlist={handleShortlist}
                  onScheduleInterview={handleScheduleInterview}
                />
              ))}
            </div>
          </div>

          {/* Upcoming Interviews */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Upcoming Interviews</h2>
              <Button variant="link" className="text-primary">
                View All →
              </Button>
            </div>
            <div className="space-y-3">
              {interviews.map((interview) => (
                <div
                  key={interview.id}
                  className="rounded-xl border border-border bg-card p-4 transition-all hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                        {interview.candidateName.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <p className="font-medium">{interview.candidateName}</p>
                        <p className="text-sm text-muted-foreground">{interview.position}</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="flex items-center gap-1">
                      {interview.type === 'video' && <Video className="h-3 w-3" />}
                      {interview.type === 'in-person' && <MapPin className="h-3 w-3" />}
                      {interview.type}
                    </Badge>
                  </div>
                  <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {new Date(interview.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {interview.time}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Stats Card */}
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="font-semibold">This Week</h3>
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">New Applications</span>
                  <span className="font-semibold">{dashboardStats.applicationsThisWeek}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Pending Tasks</span>
                  <span className="font-semibold">{dashboardStats.pendingTasks}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Interviews Completed</span>
                  <span className="font-semibold">5</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <CandidateProfile
        candidate={selectedCandidate}
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onShortlist={handleShortlist}
        onScheduleInterview={handleScheduleInterview}
        onAssignTask={handleAssignTask}
      />
      <ScheduleInterviewModal
        candidate={selectedCandidate}
        open={interviewOpen}
        onClose={() => setInterviewOpen(false)}
      />
      <AssignTaskModal
        candidate={selectedCandidate}
        open={taskOpen}
        onClose={() => setTaskOpen(false)}
      />
    </DashboardLayout>
  );
};

export default Index;
